import { Capacitor } from "@capacitor/core";
import { assertNever } from "@msys/common";
import React from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useMatch,
  useNavigate,
} from "react-router-dom";
import { SplashScreen } from "../common/SplashScreen.js";
import { CreateNewOrganisationPage } from "./auth/CreateNewOrganisationPage.js";
import { CurrentUserError } from "./auth/CurrentUserError.js";
import { ImplicitFlow } from "./auth/ImplicitFlow.js";
import { InvalidSelectedUserError } from "./auth/InvalidSelectedUserError.js";
import { isValidOrganisationType } from "./auth/isValidOrganisationType.js";
import { SelectUserPage } from "./auth/SelectUserPage.js";
import { SessionExpiredError } from "./auth/SessionExpiredError.js";
import { useAuth } from "./auth/useAuth.js";
import { useAvailableUsers } from "./auth/useAvailableUsers.js";
import { useSelectedUser } from "./auth/useSelectedUser.js";
import { useUserData } from "./auth/useUserData.js";
import { useUrlSearchParams } from "./commons/hooks/useUrlSearchParams.js";
import { ORGANISATION_TYPE_URL_SEARCH_PARAM } from "./constants.js";
import { useOnboardingFlow } from "./features/organisations/onboarding/useOnboardingFlow.js";
import { InstallerRoutes } from "./installer-routes/Routes.js";
import { CrmLinkingInvitation } from "./invitation/CrmLinkingInvitation.js";
import { InvitationPage } from "./invitation/InvitationPage.js";
import { OrganisationMembershipInvitation } from "./invitation/OrganisationMembershipInvitation.js";
import { ProjectContracteeInvitation } from "./invitation/ProjectContracteeInvitation.js";
import { useInvitationTokenContext } from "./invitation/useInvitationTokenContext.js";
import { LandingPage } from "./LandingPage.js";
import { M1RedirectRoute } from "./main-routes/M1RedirectRoute.js";
import { MainRoutes } from "./main-routes/Routes.js";
import { SapSalesQuotationQuoteRedirectRoute } from "./main-routes/SapSalesQuotationQuoteRedirectRoute.js";
import { OrganisationPublicRoutes } from "./public-routes/OrganisationPublicRoutes.js";

export const AppRoutes = () => {
  const location = useLocation();
  const { isReady, isAuthenticated } = useAuth();

  const {
    token,
    tokenData,
    isLoading: invitationTokenDataIsLoading,
  } = useInvitationTokenContext();

  if (!isReady || invitationTokenDataIsLoading) {
    return <SplashScreen />;
  }

  if (
    !isAuthenticated &&
    token &&
    tokenData &&
    tokenData?.__typename !== "ReferralInfo"
  ) {
    return (
      <InvitationPage isAuthenticated={false}>
        {tokenData.__typename === "OrganisationMembershipInvitation" ? (
          <OrganisationMembershipInvitation
            isAuthenticated={false}
            invitation={tokenData}
          />
        ) : tokenData.__typename === "ProjectContracteeInvitation" ? (
          <ProjectContracteeInvitation
            isAuthenticated={false}
            invitation={tokenData}
          />
        ) : tokenData.__typename === "CrmLinkingInvitation" ? (
          <CrmLinkingInvitation
            isAuthenticated={false}
            invitation={tokenData}
          />
        ) : (
          assertNever(tokenData)
        )}
      </InvitationPage>
    );
  }

  return (
    <Routes>
      {/* LEGACY REDIRECTS */}
      <Route
        path="/authenticate"
        element={
          <Navigate to={{ pathname: "/next", search: location.search }} />
        }
      />
      <Route
        path="/authenticate/login"
        element={
          <Navigate to={{ pathname: "/next", search: location.search }} />
        }
      />
      <Route
        path="/authenticate/signup"
        element={
          <Navigate to={{ pathname: "/next", search: location.search }} />
        }
      />
      <Route
        path="/authenticate/signup/craftsman"
        element={
          <Navigate to={{ pathname: "/next", search: location.search }} />
        }
      />
      <Route
        path="/authenticate/signup/client"
        element={
          <Navigate to={{ pathname: "/next", search: location.search }} />
        }
      />
      {/* LEGACY REDIRECTS */}

      <Route index element={<LandingPage />} />
      <Route path="/org/*" element={<OrganisationPublicRoutes />} />
      <Route path={"*"} element={<AuthenticatedRoutes />} />
    </Routes>
  );
};

const AuthenticatedRoutes = () => {
  const { auth, isReady, isAuthenticated, belongsToGroup } = useAuth();
  const { selectedUserId, setSelectedUserId } = useSelectedUser();
  const {
    availableUsers,
    isLoading: availableUsersIsLoading,
    refetch: refetchAvailableUsers,
  } = useAvailableUsers();
  const {
    token,
    tokenData,
    isLoading: invitationTokenDataIsLoading,
  } = useInvitationTokenContext();
  const navigate = useNavigate();

  const { urlSearchParams } = useUrlSearchParams();
  const organisationType =
    urlSearchParams.get(ORGANISATION_TYPE_URL_SEARCH_PARAM) ?? "CRAFTSMAN";
  if (!isValidOrganisationType(organisationType)) {
    throw new Error("Invalid organisation type");
  }

  const { setOnboardingFlowStage } = useOnboardingFlow();

  React.useEffect(() => {
    if (
      !selectedUserId &&
      availableUsers.length === 1 &&
      !belongsToGroup("org-creator")
    ) {
      setSelectedUserId(availableUsers[0].id);
    }
  }, [availableUsers, belongsToGroup, selectedUserId, setSelectedUserId]);

  React.useEffect(() => {
    if (isReady && !isAuthenticated) {
      if (Capacitor.isNativePlatform()) {
        navigate("/");
      } else {
        auth.login();
      }
    }
  }, [auth, isAuthenticated, isReady, navigate]);

  if (!isAuthenticated) {
    return <SplashScreen />;
  }

  if (invitationTokenDataIsLoading) {
    return <SplashScreen />;
  }

  if (
    selectedUserId &&
    !availableUsersIsLoading &&
    availableUsers.every(user => user.id !== selectedUserId)
  ) {
    return <InvalidSelectedUserError />;
  }

  if (
    token &&
    tokenData &&
    tokenData.__typename === "OrganisationMembershipInvitation"
  ) {
    return (
      <InvitationPage isAuthenticated={true}>
        <OrganisationMembershipInvitation
          invitation={tokenData}
          isAuthenticated={true}
        />
      </InvitationPage>
    );
  }

  if (
    !availableUsersIsLoading &&
    availableUsers.length === 0 &&
    token &&
    tokenData &&
    tokenData?.__typename === "ProjectContracteeInvitation" &&
    organisationType === "CLIENT"
  ) {
    return (
      <ImplicitFlow
        invitationToken={tokenData.invitationToken}
        organisationName={`${auth.idTokenParsed?.given_name ?? ""} ${auth.idTokenParsed?.family_name ?? ""}`}
      />
    );
  }

  if (!availableUsersIsLoading && availableUsers.length === 0) {
    return (
      <CreateNewOrganisationPage
        referral={
          token && tokenData?.__typename === "ReferralInfo"
            ? { token, data: tokenData }
            : null
        }
        onComplete={userId => {
          setOnboardingFlowStage("form");
          setSelectedUserId(userId);
        }}
      />
    );
  }

  if (token && tokenData) {
    if (tokenData?.__typename === "ProjectContracteeInvitation") {
      return (
        <InvitationPage isAuthenticated={true}>
          <ProjectContracteeInvitation
            invitation={tokenData}
            isAuthenticated={true}
            availableUsers={availableUsers}
            availableUsersIsLoading={availableUsersIsLoading}
          />
        </InvitationPage>
      );
    } else if (tokenData?.__typename === "CrmLinkingInvitation") {
      return (
        <InvitationPage isAuthenticated={true}>
          <CrmLinkingInvitation
            invitation={tokenData}
            isAuthenticated={true}
            availableUsers={availableUsers}
            availableUsersIsLoading={availableUsersIsLoading}
          />
        </InvitationPage>
      );
    }
  }

  if (!selectedUserId) {
    return (
      <SelectUserPage
        availableUsers={availableUsers}
        availableUsersIsLoading={availableUsersIsLoading}
        onMount={refetchAvailableUsers}
      />
    );
  }

  return <UserRoutes />;
};

const UserRoutes = () => {
  const { isAuthenticated } = useAuth();
  const {
    isLoading: userDataIsLoading,
    currentUser,
    appViewerRole,
  } = useUserData();
  const m1RedirectMatch = useMatch(
    `/partner/m1plus/:m1AccountId/quote/:m1OfferNumber`
  );
  const sapSalesQuotationMatch = useMatch(
    `/partner/sap/sales-quotation/quote/:salesQuotationId`
  );

  if (userDataIsLoading) {
    return <SplashScreen />;
  }

  if (!currentUser || !appViewerRole) {
    if (!isAuthenticated) {
      return <SessionExpiredError />;
    }

    return <CurrentUserError />;
  }

  if (
    m1RedirectMatch &&
    m1RedirectMatch.params.m1AccountId &&
    m1RedirectMatch.params.m1OfferNumber
  ) {
    return (
      <M1RedirectRoute
        m1AccountId={Number(m1RedirectMatch.params.m1AccountId)}
        m1OfferNumber={m1RedirectMatch.params.m1OfferNumber}
      />
    );
  }
  if (
    sapSalesQuotationMatch &&
    sapSalesQuotationMatch.params.salesQuotationId
  ) {
    return (
      <SapSalesQuotationQuoteRedirectRoute
        salesQuotationId={sapSalesQuotationMatch.params.salesQuotationId}
      />
    );
  }

  // use a separate router for installers
  if (appViewerRole === "INSTALLER") {
    return <InstallerRoutes />;
  }

  return <MainRoutes />;
};
